/**
 * L1J Web Client - TargetPrompt Component
 * 대상 선택 안내 배너 (+ 문자열 입력)
 *
 * 서버 `C_UseItem`(opcode 44)은 아이템의 use_type에 따라 itemObjId 뒤에서
 * "무엇을 골랐는지"를 이어서 읽는다 (대상 아이템 objId / 대상 캐릭터 objId /
 * 타일 좌표 / 문자열). 고르는 동안 무엇을 기다리는 중인지 화면에 남기지 않으면
 * 클릭이 먹통이 된 것처럼 보이므로, 이 배너가 그 상태를 표시한다.
 *
 * 배너 자체는 상태를 갖지 않는다 — 어떤 아이템을 쓰는 중인지, 취소하면 무엇을
 * 되돌려야 하는지는 GameScene의 대상 선택 상태 머신이 전부 들고 있다.
 */

import Phaser from 'phaser';

export interface TargetPromptCallbacks {
  /** 문자열 입력 모드에서 Enter를 눌렀을 때. 빈 문자열은 전달하지 않는다. */
  onSubmitText?: (text: string) => void;
  /** 문자열 입력 모드에서 ESC를 눌렀을 때. */
  onCancel?: () => void;
}

/** 배너 폭/높이. 화면 상단 중앙에 고정한다. */
const PANEL_WIDTH = 460;
const PANEL_HEIGHT = 40;

/** 안내 문구 색 (대상 선택 중임이 한눈에 읽히도록 강조색). */
const PROMPT_TEXT_COLOR = '#ffd700';

export class TargetPrompt extends Phaser.GameObjects.Container {
  private callbacks: TargetPromptCallbacks;

  private panelBg!: Phaser.GameObjects.Rectangle;
  private messageText!: Phaser.GameObjects.Text;

  /** 문자열 입력용 DOM 요소. ChatBox와 같은 이유로 씬 최상위에 붙인다. */
  private textInput!: HTMLInputElement;
  private textInputContainer!: Phaser.GameObjects.DOMElement;
  private textInputVisible: boolean = false;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    callbacks?: TargetPromptCallbacks
  ) {
    super(scene, x, y);
    this.callbacks = callbacks || {};
    scene.add.existing(this);

    // 카메라 스크롤과 무관하게 화면에 고정한다.
    this.setScrollFactor(0);

    this.createUI();
    this.createTextInput(x, y);

    this.setVisible(false);
    // 인벤토리(150)/메뉴보다 위에 떠야 한다 — 안내가 가려지면 의미가 없다.
    this.setDepth(300);
  }

  private createUI(): void {
    this.panelBg = this.scene.add.rectangle(0, 0, PANEL_WIDTH, PANEL_HEIGHT, 0x1a1a2e, 0.95);
    this.panelBg.setStrokeStyle(2, 0xffd700);
    this.panelBg.setScrollFactor(0);
    this.add(this.panelBg);

    this.messageText = this.scene.add.text(0, 0, '', {
      fontSize: '13px',
      fontFamily: 'Arial, sans-serif',
      color: PROMPT_TEXT_COLOR,
      align: 'center',
      wordWrap: { width: PANEL_WIDTH - 24 }
    });
    this.messageText.setOrigin(0.5);
    this.messageText.setScrollFactor(0);
    this.add(this.messageText);
  }

  /**
   * 문자열 입력창 생성 (use_type 16 변신 스크롤).
   *
   * Phaser 키보드 매니저는 window에 리스너를 걸기 때문에, 입력 중 눌린 키가
   * 씬의 단축키(I/E/C/M/ESC)로도 해석된다. ChatBox와 동일하게 keydown에서
   * stopPropagation()으로 막는다.
   */
  private createTextInput(x: number, y: number): void {
    this.textInput = document.createElement('input');
    this.textInput.type = 'text';
    this.textInput.maxLength = 40;
    this.textInput.style.cssText = `
      width: ${PANEL_WIDTH - 40}px;
      padding: 5px 10px;
      font-size: 13px;
      font-family: Arial, sans-serif;
      background: #0a0a15;
      border: 1px solid #6a6aaa;
      border-radius: 4px;
      color: #ffffff;
      outline: none;
    `;

    this.textInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        const value = this.textInput.value.trim();
        if (value.length === 0) return; // 빈 입력은 무시 (서버가 읽을 문자열이 없다)
        this.callbacks.onSubmitText?.(value);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.callbacks.onCancel?.();
      }
    });

    // 위치는 컨테이너 로컬이 아니라 씬 절대 좌표다 (ChatBox와 동일한 규약).
    this.textInputContainer = this.scene.add.dom(x, y + PANEL_HEIGHT / 2 + 22, this.textInput);
    this.textInputContainer.setScrollFactor(0);
    this.textInputContainer.setVisible(false);
    this.textInputContainer.setDepth(300);

    // 씬이 재시작돼도 DOM 요소가 남지 않도록 함께 파괴한다.
    this.once(Phaser.GameObjects.Events.DESTROY, () => {
      this.textInputContainer.destroy();
    });
  }

  /**
   * 안내만 띄운다 (인벤토리/월드 엔티티/지면 선택 모드).
   */
  public showMessage(message: string): void {
    this.hideTextInput();
    this.messageText.setText(message);
    this.setVisible(true);
  }

  /**
   * 문자열 입력을 받는다 (use_type 16). Enter로 확정, ESC로 취소.
   */
  public showTextInput(message: string, placeholder: string = ''): void {
    this.messageText.setText(message);
    this.setVisible(true);

    this.textInput.value = '';
    this.textInput.placeholder = placeholder;
    this.textInputContainer.setVisible(true);
    this.textInputVisible = true;
    // 표시 직후에 포커스를 주면 Phaser의 캔버스 포커스와 경합할 수 있어 한 틱 미룬다.
    this.scene.time.delayedCall(0, () => {
      if (this.textInputVisible) this.textInput.focus();
    });
  }

  /** 배너와 입력창을 함께 닫는다. */
  public hide(): void {
    this.hideTextInput();
    this.setVisible(false);
  }

  /** 문자열 입력창이 떠 있는지. 씬이 ESC를 가로챌지 판단할 때 쓴다. */
  public isTextInputActive(): boolean {
    return this.textInputVisible;
  }

  /**
   * 배너 폭/높이. 씬이 "이 클릭이 월드가 아니라 UI 위였는지"를 판정할 때 쓴다 —
   * 지면 선택 중에 배너를 눌러 엉뚱한 타일에 아이템을 써 버리는 사고를 막는다.
   */
  public getPanelWidth(): number {
    return PANEL_WIDTH;
  }

  public getPanelHeight(): number {
    return PANEL_HEIGHT;
  }

  private hideTextInput(): void {
    if (!this.textInputVisible) return;
    this.textInputVisible = false;
    this.textInput.blur();
    this.textInputContainer.setVisible(false);
  }
}
